import axios from 'axios'

const API_URL = process.env.VUE_APP_AI_AGENT_API_BASE_URL

function getAuthToken () {
  return localStorage.getItem('accessToken')
}

class ChatService {
  sendMessage (sessionId, messageData) {
    return axios.post(API_URL + 'chat/sessions/' + sessionId + '/messages', messageData)
  }

  generateSessionTitle (sessionId) {
    return axios.post(API_URL + 'chat/sessions/' + sessionId + '/generate-title')
  }

  /**
   * Streaming message via SSE (fetch-based so we can pass AbortSignal).
   * Yields parsed AgentEvent objects as they arrive.
   * @param {number} sessionId
   * @param {{ content: string, maxIterations?: number, maxWallTimeSeconds?: number, maxCostUsd?: number }} messageData
   * @param {AbortSignal} abortSignal
   * @param {string|null} hostContextJson - JSON string for X-Agent-Context header (fresh context for this turn)
   * @yields {object} parsed AgentEvent
   */
  async * streamMessage (sessionId, messageData, abortSignal, hostContextJson = null) {
    const url = API_URL + 'chat/sessions/' + sessionId + '/messages/stream'
    const token = getAuthToken()

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
        ...(hostContextJson ? { 'X-Agent-Context': hostContextJson } : {})
      },
      body: JSON.stringify(messageData),
      signal: abortSignal
    })

    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`Stream request failed: ${response.status} ${text}`)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        // Keep the last (possibly incomplete) line in the buffer
        buffer = lines.pop()

        let eventType = null

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            const raw = line.slice(5).trim()
            if (!raw) continue
            try {
              const parsed = JSON.parse(raw)
              if (eventType) parsed.$type = eventType
              yield parsed
            } catch {
              // malformed JSON — skip
            }
            eventType = null
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
  }

  confirmRunStep (runId, stepId, approved) {
    return axios.post(API_URL + 'chat/runs/' + runId + '/confirm', { stepId, approved })
  }

  getRun (runId) {
    return axios.get(API_URL + 'chat/runs/' + runId)
  }
}

export default new ChatService()
